import { Suspense, lazy } from "react";
import Logo from "./components/Logo";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";

const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SignIn = lazy(() => import("./components/auth/SignIn"));
const SignUp = lazy(() => import("./components/auth/SignUp"));
const AuthRecovery = lazy(() => import("./pages/AuthRecovery"));
const JobCards = lazy(() => import("./pages/JobCards"));
const JobCardFormPage = lazy(() => import("./pages/JobCardFormPage"));
const JobCardDetail = lazy(() => import("./pages/JobCardDetail"));
const JobCardEditPage = lazy(() => import("./pages/JobCardEditPage"));
const Consultations = lazy(() => import("./pages/Consultations"));
const ConsultationForm = lazy(() => import("./pages/ConsultationForm"));
const FieldHome = lazy(() => import("./pages/FieldHome"));
const PipelinePage = lazy(() => import("./pages/PipelinePage"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const ChecksPage = lazy(() => import("./pages/ChecksPage"));
const CheckDetailPage = lazy(() => import("./pages/CheckDetailPage"));
const VehicleChecklistPage = lazy(() => import("./pages/VehicleChecklistPage"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const UserGuide = lazy(() => import("./pages/UserGuide"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <ErrorBoundary>
        <BrowserRouter>
          <Suspense
            fallback={
              <div className="min-h-screen flex flex-col items-center justify-center gap-4">
                <Logo size="md" className="animate-pulse" />
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            }
          >
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/auth-recovery" element={<AuthRecovery />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />

              {/* Protected routes - Map (requires office/supervisor/admin) */}
              <Route
                path="/app"
                element={
                  <ProtectedRoute requireMap>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes - Field workspace (foreman/viewer) */}
              <Route
                path="/field-home"
                element={
                  <ProtectedRoute allowedRoles={['foreman', 'viewer']}>
                    <FieldHome />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes - Operations planning */}
              <Route
                path="/pipeline"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
                    <PipelinePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'supervisor', 'office', 'foreman', 'viewer']}>
                    <CalendarPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/checks"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'supervisor', 'office', 'foreman', 'viewer']}>
                    <ChecksPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/checks/:type/:id"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'supervisor', 'office', 'foreman', 'viewer']}>
                    <CheckDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/checklists/vehicle"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'supervisor', 'office', 'foreman', 'viewer']}>
                    <VehicleChecklistPage />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes - Job Cards */}
              <Route
                path="/job-cards"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'supervisor', 'office', 'foreman', 'viewer']}>
                    <JobCards />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/job-cards/new"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'supervisor', 'office', 'foreman']}>
                    <JobCardFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/job-cards/:id"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'supervisor', 'office', 'foreman', 'viewer']}>
                    <JobCardDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/job-cards/:id/edit"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'supervisor', 'office', 'foreman']}>
                    <JobCardEditPage />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes - Consultations */}
              <Route
                path="/consultations"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'supervisor', 'office']}>
                    <Consultations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/consultations/new"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'supervisor', 'office']}>
                    <ConsultationForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/consultations/:id"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'supervisor', 'office']}>
                    <ConsultationForm />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes - User Guide (all authenticated users) */}
              <Route
                path="/guide"
                element={
                  <ProtectedRoute>
                    <UserGuide />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes - Integrations (admin only) */}
              <Route
                path="/integrations"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <IntegrationsPage />
                  </ProtectedRoute>
                }
              />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ErrorBoundary>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
